import type { InternalAxiosRequestConfig } from "axios";
import axios from "axios";
import { setupInterceptorsTo } from "./interceptorSetup";
import { getAppMeta } from "@/helper/helper";

export const getDynamicBaseUrl = () => {
  if (typeof window === "undefined") return process.env.NEXT_PUBLIC_BASE_URL; // Server-side fallback

  const hostname = window.location.hostname;

  // Check for Stratagile domain
  if (hostname.includes("stratagileai")) {
    return process.env.NEXT_PUBLIC_API_URL_STRATAGILE;
  }

  // Default to mple.ai (fallback)
  return process.env.NEXT_PUBLIC_BASE_URL;
};

export const getDynamicSocketUrl = () => {
  // Server-side: always use default
  if (typeof window === "undefined") return process.env.NEXT_PUBLIC_SOCKET_URL;

  try {
    // Try to get iframe domain from URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const iframeDomain = urlParams.get("_iframe_domain");

    // Use iframe domain if available, otherwise use current hostname
    const hostname = iframeDomain || window.location.hostname;

    // Check for Stratagile domain
    if (hostname && hostname.includes("stratagileai")) {
      return (
        process.env.NEXT_PUBLIC_SOCKET_URL_STRATAGILE ||
        process.env.NEXT_PUBLIC_SOCKET_URL
      );
    }
  } catch (error) {
    // If anything fails, fall back to default
    console.warn("Error determining socket URL, using default:", error);
  }

  // Default fallback
  return process.env.NEXT_PUBLIC_SOCKET_URL;
};

export const baseURL = getDynamicBaseUrl();

const isBrowser = typeof window !== "undefined";

export const commonRequestInterceptor = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  if (!isBrowser) return config;

  const appMeta = getAppMeta();

  if (appMeta) {
    config.headers["x-app-name"] = appMeta.app;
    config.headers["x-app-platform"] = appMeta.platform;
    config.headers["x-app-version"] = appMeta.version;
    config.headers["x-app-build"] = appMeta.build;
    config.headers["x-app-os"] = appMeta.osVersion;
    config.headers["x-request-source"] = "webview";
  }

  return config;
};

export const globalAxiosInstance = axios.create({
  baseURL,
});
globalAxiosInstance.interceptors.request.use(commonRequestInterceptor);

export const protectedAxiosInstance = axios.create({
  baseURL,
});
protectedAxiosInstance.interceptors.request.use(commonRequestInterceptor);
setupInterceptorsTo(protectedAxiosInstance);
